import { useState } from 'react';
import { useApi, useMutation } from '../hooks/useApi';
import SalaryGauge from '../components/SalaryGauge';
import type { SalaryBalance, Invoice, Plan, PlanType } from '../types';
import './BillingPage.css';

// ─── 플랜 데이터 ─────────────────────────────────────────────────────────────

const PLANS: Plan[] = [
  {
    id: 'starter',
    name: '스타터',
    monthlyPrice: 9900,
    salaryBalance: 59000,
    features: ['1개 쇼핑몰 연결', '기본 워커 4종', '일 200건 주문 처리', '이메일 지원'],
  },
  {
    id: 'business',
    name: '비즈니스',
    monthlyPrice: 29900,
    salaryBalance: 179000,
    features: ['3개 쇼핑몰 연결', '전체 워커 6종', '무제한 주문 처리', '멀티 플랫폼 동기화', '우선 지원'],
  },
  {
    id: 'pro',
    name: '프로',
    monthlyPrice: 59900,
    salaryBalance: 359000,
    features: ['무제한 쇼핑몰 연결', '전체 워커 6종', '무제한 주문 처리', '전용 분석 리포트', '전담 매니저'],
  },
];

// ─── 더미 데이터 ──────────────────────────────────────────────────────────────

const MOCK_BALANCE: SalaryBalance = {
  current: 18400,
  total: 59000,
  used: 40600,
  percentage: 68.8,
  resetDate: '2026-04-01',
};

const MOCK_INVOICES: Invoice[] = [
  {
    id: 'inv-001',
    period: '2026-03',
    amount: 9900,
    status: 'paid',
    issuedAt: '2026-03-01T00:00:00Z',
    paidAt: '2026-03-01T10:23:00Z',
  },
  {
    id: 'inv-002',
    period: '2026-02',
    amount: 9900,
    status: 'paid',
    issuedAt: '2026-02-01T00:00:00Z',
    paidAt: '2026-02-01T09:15:00Z',
  },
  {
    id: 'inv-003',
    period: '2026-01',
    amount: 9900,
    status: 'paid',
    issuedAt: '2026-01-01T00:00:00Z',
    paidAt: '2026-01-01T11:42:00Z',
  },
];

const MOCK_CURRENT_PLAN: PlanType = 'starter';

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${year}년 ${Number(month)}월`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const STATUS_LABEL: Record<Invoice['status'], string> = {
  paid: '납부 완료',
  unpaid: '미납',
  pending: '처리 중',
};

const STATUS_CLASS: Record<Invoice['status'], string> = {
  paid: 'badge-success',
  unpaid: 'badge-danger',
  pending: 'badge-warning',
};

// ─── 추가 예산 모달 ───────────────────────────────────────────────────────────

interface AddBudgetModalProps {
  onClose: () => void;
  onConfirm: (amount: number) => Promise<void>;
  isLoading: boolean;
}

const BUDGET_OPTIONS = [10000, 30000, 50000, 100000];

function AddBudgetModal({ onClose, onConfirm, isLoading }: AddBudgetModalProps) {
  const [selected, setSelected] = useState(30000);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setError('');
    try {
      await onConfirm(selected);
      onClose();
    } catch {
      setError('결제 처리 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 className="modal__title">추가 예산 배정</h3>
          <button className="modal__close" onClick={onClose} type="button">✕</button>
        </div>

        <div className="modal__body">
          <p className="modal__desc">
            추가 예산을 배정하면 AI 팀원들이 더 많은 작업을 처리할 수 있습니다.
          </p>

          <div className="budget-options">
            {BUDGET_OPTIONS.map((amount) => (
              <button
                key={amount}
                className={`budget-option ${selected === amount ? 'selected' : ''}`}
                onClick={() => setSelected(amount)}
                type="button"
              >
                {amount.toLocaleString('ko-KR')}원
              </button>
            ))}
          </div>

          {error && <div className="alert alert-error">{error}</div>}
        </div>

        <div className="modal__footer">
          <button className="btn btn-secondary" onClick={onClose} type="button">
            취소
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void handleConfirm()}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? <span className="spinner" /> : `${selected.toLocaleString('ko-KR')}원 결제`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 메인 페이지 ─────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } =
    useApi<SalaryBalance>('/billing/balance');
  const { data: invoices, isLoading: invoicesLoading } =
    useApi<Invoice[]>('/billing/invoices');
  const { data: currentPlan } =
    useApi<{ plan: PlanType }>('/billing/plan');

  const { mutate: addBudget, isLoading: addingBudget } = useMutation<void, { amount: number }>(
    '/billing/add-budget'
  );
  const { mutate: changePlan, isLoading: changingPlan } = useMutation<void, { plan: PlanType }>(
    '/billing/plan',
    'PUT'
  );

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [changingTo, setChangingTo] = useState<PlanType | null>(null);

  const displayBalance = balance ?? MOCK_BALANCE;
  const displayInvoices = invoices ?? MOCK_INVOICES;
  const displayPlan = currentPlan?.plan ?? MOCK_CURRENT_PLAN;

  const handleAddBudget = async (amount: number) => {
    await addBudget({ amount });
    void refetchBalance();
  };

  const handleChangePlan = async (plan: PlanType) => {
    if (plan === displayPlan) return;
    setChangingTo(plan);
    await changePlan({ plan });
    setChangingTo(null);
  };

  return (
    <div className="billing-page">
      {/* 헤더 */}
      <div className="billing-page__header">
        <h2 className="billing-page__title">플랜 & 급여 관리</h2>
      </div>

      {/* 급여 게이지 */}
      <div className="billing-page__gauge">
        <SalaryGauge balance={displayBalance} isLoading={balanceLoading} />
        <div className="billing-page__add-budget">
          <button
            className="btn btn-primary"
            onClick={() => setShowBudgetModal(true)}
            type="button"
          >
            💰 추가 예산 배정
          </button>
        </div>
      </div>

      {/* 플랜 변경 */}
      <section className="billing-page__section">
        <h3 className="billing-page__section-title">플랜 관리</h3>
        <div className="plan-list">
          {PLANS.map((plan) => {
            const isCurrent = plan.id === displayPlan;
            const isChangingThis = changingTo === plan.id;

            return (
              <div
                key={plan.id}
                className={`plan-item ${isCurrent ? 'current' : ''}`}
              >
                <div className="plan-item__info">
                  <div className="plan-item__name-row">
                    <span className="plan-item__name">{plan.name}</span>
                    {isCurrent && (
                      <span className="badge badge-primary">현재 플랜</span>
                    )}
                  </div>
                  <div className="plan-item__price">
                    월 {plan.monthlyPrice.toLocaleString('ko-KR')}원
                    <span className="plan-item__salary">
                      (급여 {plan.salaryBalance.toLocaleString('ko-KR')}원)
                    </span>
                  </div>
                  <ul className="plan-item__features">
                    {plan.features.map((f) => (
                      <li key={f}>✓ {f}</li>
                    ))}
                  </ul>
                </div>
                {!isCurrent && (
                  <button
                    className={`btn ${
                      PLANS.findIndex(p => p.id === plan.id) >
                      PLANS.findIndex(p => p.id === displayPlan)
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                    onClick={() => void handleChangePlan(plan.id)}
                    disabled={changingPlan}
                    type="button"
                  >
                    {isChangingThis ? (
                      <span className="spinner" />
                    ) : PLANS.findIndex(p => p.id === plan.id) >
                      PLANS.findIndex(p => p.id === displayPlan) ? (
                      '업그레이드'
                    ) : (
                      '다운그레이드'
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 청구서 목록 */}
      <section className="billing-page__section">
        <h3 className="billing-page__section-title">청구서 내역</h3>

        {invoicesLoading ? (
          <div className="invoice-skeleton">
            {[1, 2, 3].map((i) => (
              <div key={i} className="invoice-skeleton__item" />
            ))}
          </div>
        ) : (
          <div className="invoice-list">
            {displayInvoices.length === 0 ? (
              <div className="billing-page__empty">
                <span>📄</span>
                <p>청구서가 없습니다.</p>
              </div>
            ) : (
              displayInvoices.map((invoice) => (
                <div key={invoice.id} className="invoice-item">
                  <div className="invoice-item__period">
                    {formatPeriod(invoice.period)}
                  </div>
                  <div className="invoice-item__amount">
                    {invoice.amount.toLocaleString('ko-KR')}원
                  </div>
                  <div className="invoice-item__date">
                    {invoice.paidAt ? formatDate(invoice.paidAt) : formatDate(invoice.issuedAt)}
                  </div>
                  <span className={`badge ${STATUS_CLASS[invoice.status]}`}>
                    {STATUS_LABEL[invoice.status]}
                  </span>
                  {invoice.downloadUrl && (
                    <a
                      href={invoice.downloadUrl}
                      className="btn btn-secondary btn-sm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      다운로드
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* 추가 예산 모달 */}
      {showBudgetModal && (
        <AddBudgetModal
          onClose={() => setShowBudgetModal(false)}
          onConfirm={handleAddBudget}
          isLoading={addingBudget}
        />
      )}
    </div>
  );
}
